import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Download } from "lucide-react";
import api, { formatDetail, BACKEND_URL } from "@/lib/api";
import { getLeadAttribution } from "@/lib/attribution";
import { SectionEyebrow } from "@/components/Marquee";

export default function LeadMagnet() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  async function onSubmit(e) {
    e.preventDefault();
    if (name.trim().length < 2 || phone.trim().length < 6) {
      toast.error("Please enter a valid name and phone.");
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.post("/leads/checklist", { name, phone, ...getLeadAttribution() });
      setDone(data);
      toast.success("Your checklist is ready. Downloading…");
      // trigger download
      window.open(`${BACKEND_URL}/api/leads/checklist/${data.id}/download`, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="checklist" className="relative py-20 md:py-28 bg-[#0A0A0F]" data-testid="lead-magnet-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <SectionEyebrow n="Ch. XI" testId="lead-magnet-eyebrow">Free Playbook</SectionEyebrow>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.8, 0, 0.2, 1] }}
            className="mt-6 font-serif text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight"
          >
            The 40-point <span className="italic gradient-text">buying checklist.</span>
          </motion.h2>
          <p className="mt-6 text-silver/70 max-w-xl leading-relaxed">
            The same PDI + negotiation checklist our advisors carry to every deal. Yours free — no spam,
            just the sheet.
          </p>
        </div>
        <div className="lg:col-span-6">
          <form
            onSubmit={onSubmit}
            className="relative rounded-3xl gradient-border bg-[#141323] p-8 md:p-10 overflow-hidden"
            data-testid="checklist-form"
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40 blur-3xl gradient-bg pointer-events-none" />
            <div className="relative">
              <label className="block eyebrow gradient-text">Your Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Rohan Malhotra"
                className="mt-3 h-12 bg-void border-white/10 focus-visible:ring-signal text-white"
                data-testid="checklist-name"
              />
              <label className="block eyebrow gradient-text mt-6">Phone</label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98XXX XXXXX"
                className="mt-3 h-12 bg-void border-white/10 focus-visible:ring-signal text-white"
                data-testid="checklist-phone"
              />
              <button
                type="submit"
                disabled={loading}
                className="mt-8 w-full py-4 rounded-full font-medium text-white gradient-bg disabled:opacity-60 hover:scale-[1.01] transition-transform inline-flex items-center justify-center gap-2"
                data-testid="checklist-submit"
              >
                {loading ? "Preparing…" : (
                  <>
                    <Download className="w-4 h-4" />
                    Download the Checklist
                  </>
                )}
              </button>
              {done && (
                <a
                  href={`${BACKEND_URL}/api/leads/checklist/${done.id}/download`}
                  className="mt-4 block text-center text-xs text-silver/70 underline"
                  data-testid="checklist-manual-link"
                >
                  Didn't open? Click to download.
                </a>
              )}
              <p className="mt-4 text-[11px] text-silver/40">
                By downloading you agree we may WhatsApp you the sheet + one buyer-side check-in. No spam.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
